




































import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmployees, requestLeave } from '../../redux/employer-employeeSlice/EmployerEmployeeSlice';
import { BiX, BiDownload } from 'react-icons/bi';
import { toast } from 'sonner';
import EmployeeAttendance from './EmployeeAttendance';
import axios from 'axios';
import { Chart as ChartJS, ArcElement, BarElement, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend } from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(ArcElement, BarElement, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

const EmployerEmployeeJobSeeker = () => {
  const dispatch = useDispatch();
  const [benefits, setBenefits] = useState([]);
  const { employees, status, error } = useSelector((state) => state.employerEmployee || {});
  const { currentUser } = useSelector((state) => state.user || {});
  const [employee, setEmployee] = useState(null);
  const [isPayslipModalOpen, setIsPayslipModalOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [isLeaveSubmitting, setIsLeaveSubmitting] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [Error, setError] = useState(null);
  const waveCanvasRef = useRef(null);
  const [mockAttendanceRecords, setMockAttendanceRecords] = useState(new Map());

  useEffect(() => {
    if (!currentUser?._id) {
      toast.error("User not authenticated");
      setLoading(false);
      return;
    }

    const fetchBenefits = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}benefit/getUserBenefit`,
          { applicantId: currentUser._id },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setBenefits(response.data.data || []);
      } catch (err) {
        setError("Failed to load benefits");
        toast.error("Failed to load benefits");
      } finally {
        setLoading(false);
      }
    };
    fetchBenefits();
  }, [currentUser?._id]);

  useEffect(() => {
    if (!currentUser?._id) {
      console.warn('No currentUser._id found');
      return;
    }

    console.log('Fetching employee for jobSeekerId:', currentUser._id);
    console.log('Current Redux state:', { employees, status, error: "No data found" });

    dispatch(fetchEmployees({ id: currentUser._id, userType: 'jobSeeker' }))
      .unwrap()
      .then((data) => {
        console.log('Fetched data from thunk:', data);
        const employeeData = Array.isArray(data) ? data.find((emp) => emp._id === currentUser._id) : data;
        if (employeeData && employeeData._id) {
          setEmployee(employeeData);
          console.log('Updated local employee state:', employeeData);
          // Initialize mockAttendanceRecords when employee is set
          setMockAttendanceRecords(new Map([
            [employeeData._id, new Map([
              ['2025-07-28', { status: 'present', timestamp: new Date('2025-07-28T10:00:00') }],
              ['2025-07-27', { status: 'absent', timestamp: new Date('2025-07-27T23:59:59') }],
              ['2025-06-15', { status: 'present', timestamp: new Date('2025-06-15T09:00:00') }],
            ])]
          ]));
        } else {
          console.warn('No matching employee found in data:', data);
          setEmployee(null);
        }
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        setNotification({ message: 'Failed to load employee data', type: 'error' });
      });
  }, [currentUser?._id, dispatch]);

  // Water Wave Chart Animation
  useEffect(() => {
    const canvas = waveCanvasRef.current;
    if (!canvas || !employee?.leaveRequests) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let phase = 0;

    const totalLeaveDays = employee.leaveRequests.reduce((acc, leave) => {
      const start = new Date(leave.startDate);
      const end = new Date(leave.endDate);
      const days = (end - start) / (1000 * 60 * 60 * 24) + 1;
      return acc + (leave.status === 'Approved' ? days : 0);
    }, 0);
    const waveHeight = totalLeaveDays > 0 ? Math.min(totalLeaveDays * 5, 100) : 10;

    const drawWave = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(249, 115, 22, 0.2)'; // orange-500
      ctx.beginPath();
      ctx.moveTo(0, canvas.height);

      for (let x = 0; x < canvas.width; x++) {
        const y = canvas.height / 2 + Math.sin(x * 0.02 + phase) * 20;
        ctx.lineTo(x, y - waveHeight);
      }

      ctx.lineTo(canvas.width, canvas.height);
      ctx.closePath();
      ctx.fill();

      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, 'rgba(249, 115, 22, 0.5)');
      gradient.addColorStop(1, 'rgba(249, 115, 22, 0.1)');
      ctx.fillStyle = gradient;
      ctx.fill();

      phase += 0.05;
      animationFrameId = requestAnimationFrame(drawWave);
    };

    drawWave();
    return () => cancelAnimationFrame(animationFrameId);
  }, [employee?.leaveRequests]);

  const handleRequestLeave = async (e) => {
    e.preventDefault();
    if (!employee) {
      setNotification({ message: 'No employee data available', type: 'error' });
      return;
    }

    setIsLeaveSubmitting(true);
    setNotification({ message: '', type: '' });

    try {
      const formData = {
        employerId: employee?.employer,
        employeeId: employee._id,
        ...Object.fromEntries(new FormData(e.target)),
      };

      if (formData.reason === 'Others' && formData.others) {
        formData.reason = formData.others;
        delete formData.others;
      }
      console.log('Requesting leave with formData:', formData);

      const result = await dispatch(requestLeave(formData)).unwrap();
      setNotification({ message: result.message || 'Leave request submitted successfully', type: 'success' });
      toast.success(result.message || 'Leave request submitted successfully');
      e.target.reset();
      setReason('');
    } catch (err) {
      console.error('Leave request error:', err);
      setNotification({ message: err.message || 'Failed to submit leave request', type: 'error' });
    } finally {
      setIsLeaveSubmitting(false);
    }
  };

  const calculateBreakdown = (deductions) => {
    return deductions || { tax: 0, penalty: 0, hmo: 0, other: 0 };
  };

  const generatePayslipData = (history = null) => {
    if (!employee?.salary) {
      return { 
        employee: 'N/A', 
        salary: { grossSalary: 0, deductions: {}, netSalary: 0 },
        benefits: { essential: 0, health: 0, total: 0 }
      };
    }

    const getBenefitsForPeriod = (month, year) => {
      const periodKey = `${year}-${month + 1}`;
      const monthlyBenefits = benefits
        .filter((b) => b.benefitStatus === 'Approved')
        .reduce((acc, benefit) => {
          const date = new Date(benefit.createdAt);
          const benefitPeriod = `${date.getFullYear()}-${date.getMonth() + 1}`;
          if (benefitPeriod === periodKey) {
            const amount = parseFloat(benefit.amountRequested) || 0;
            acc[benefit.benefitType] = (acc[benefit.benefitType] || 0) + amount;
          }
          return acc;
        }, { essential: 0, health: 0 });
      return {
        essential: monthlyBenefits.essential,
        health: monthlyBenefits.health,
        total: monthlyBenefits.essential + monthlyBenefits.health
      };
    };

    if (history) {
      return {
        employee: `${employee.firstName} ${employee.lastName}`,
        salary: {
          grossSalary: history.grossSalary || 0,
          deductions: history.deductions || { tax: 0, penalty: 0, hmo: 0, other: 0 },
          netSalary: history.netSalary || 0,
        },
        benefits: getBenefitsForPeriod(history.month - 1, history.year)
      };
    }

    const { gross, deductions, net, history: latestHistory } = employee.salary;
    const latest = latestHistory?.length ? latestHistory[latestHistory.length - 1] : {};
    return {
      employee: `${employee.firstName} ${employee.lastName}`,
      salary: {
        grossSalary: gross || latest.grossSalary || 0,
        deductions: deductions || latest.deductions || { tax: 0, penalty: 0, other: 0 },
        netSalary: net || latest.netSalary || 0,
      },
      benefits: getBenefitsForPeriod(latest.month - 1 || new Date().getMonth(), latest.year || new Date().getFullYear())
    };
  };

  const payslipData = selectedHistory ? generatePayslipData(selectedHistory) : generatePayslipData();

  // Pie Chart Data (Salary Breakdown)
  const pieData = {
    labels: ['Gross Salary', 'Deductions', 'Benefits'],
    datasets: [
      {
        data: [
          payslipData.salary.grossSalary || 0,
          Object.values(payslipData.salary.deductions || {}).reduce((a, b) => a + b, 0) || 0,
          payslipData.benefits.total || 0,
        ],
        backgroundColor: ['#f97316', '#1f2937', '#10b981'], // orange-500, gray-800, emerald-500
        borderColor: ['#ffffff'],
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#1f2937', font: { size: 14 } },
      },
    },
  };

  // Bar Chart Data (Salary Trends)
  const barData = {
    labels: employee?.salary?.history?.map((hist) => `${hist.month} ${hist.year}`) || [],
    datasets: [
      {
        label: 'Gross Salary (₦)',
        data: employee?.salary?.history?.map((hist) => hist.grossSalary || 0) || [],
        backgroundColor: 'rgba(249, 115, 22, 0.6)',
        borderColor: '#f97316',
        borderWidth: 1,
      },
    ],
  };

  const barOptions = {
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Amount (₦)', color: '#1f2937' },
      },
      x: {
        title: { display: true, text: 'Period', color: '#1f2937' },
      },
    },
  };

  // Line Chart Data (Attendance Trends)
  const lineData = useMemo(() => {
    const attendanceCounts = Array.from(mockAttendanceRecords.get(employee?._id) || []).reduce((acc, [date, record]) => {
      const monthYear = new Date(date).toLocaleString('default', { month: 'short', year: 'numeric' });
      if (!acc[monthYear]) acc[monthYear] = { present: 0, absent: 0 };
      acc[monthYear][record.status]++;
      return acc;
    }, {});
    return {
      labels: Object.keys(attendanceCounts),
      datasets: [
        {
          label: 'Present Days',
          data: Object.values(attendanceCounts).map((count) => count.present),
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.2)',
          fill: true,
        },
        {
          label: 'Absent Days',
          data: Object.values(attendanceCounts).map((count) => count.absent),
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.2)',
          fill: true,
        },
      ],
    };
  }, [mockAttendanceRecords, employee?._id]);

  const lineOptions = {
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { enabled: true },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Days', color: '#1f2937' },
      },
      x: {
        title: { display: true, text: 'Month', color: '#1f2937' },
      },
    },
  };

  useEffect(() => {
    if (notification.message) {
      const timer = setTimeout(() => {
        setNotification({ message: '', type: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  if (status === 'loading' && !employee) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-500"></div>
      </div>
    );
  }
  if (error && !employee) {
    return (
      <div className="flex items-center justify-center h-screen text-red-500 bg-gray-100 dark:bg-gray-900">
        {typeof error === 'string' ? error : 'An error occurred while loading data'}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white dark:bg-gradient-to-br dark:from-gray-900 dark:to-gray-800 p-6 md-overflow-x-hidden">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-8 text-center">Salaries and Leave</h2>

        {notification.message && (
          <div
            className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg text-white z-50 ${
              notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
            }`}
          >
            {notification.message}
          </div>
        )}

        {employee ? (
          <div className="space-y-8">
            {/* Charts Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pie Chart: Salary Breakdown */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-semibold text-orange-500 dark:text-orange-400 mb-4 text-center">
                  Salary Breakdown
                </h3>
                <Pie data={pieData} options={pieOptions} />
              </div>

              {/* Water Wave Chart: Total Leave Days */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-semibold text-orange-500 dark:text-orange-400 mb-4 text-center">
                  Total Approved Leave Days
                </h3>
                <canvas ref={waveCanvasRef} width={300} height={150} />
                <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Total Leave Days: {employee.leaveRequests.reduce((acc, leave) => {
                    const start = new Date(leave.startDate);
                    const end = new Date(leave.endDate);
                    const days = (end - start) / (1000 * 60 * 60 * 24) + 1;
                    return acc + (leave.status === 'Approved' ? days : 0);
                  }, 0)}
                </p>
              </div>

              {/* Bar Chart: Salary Trends */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-semibold text-orange-500 dark:text-orange-400 mb-4 text-center">
                  Salary Trends
                </h3>
                <Bar data={barData} options={barOptions} />
              </div>

              {/* Line Chart: Attendance Trends */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-semibold text-orange-500 dark:text-orange-400 mb-4 text-center">
                  Attendance Trends
                </h3>
                <Line data={lineData} options={lineOptions} />
              </div>
            </div>

            {/* Salary Details */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-semibold text-gray-700 dark:text-gray-100 mb-4">Salary Details</h3>
              <div className="overflow-x-auto">
                <table className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <thead className="bg-orange-500 text-white">
                    <tr>
                      <th className="py-3 px-4 text-left">Period</th>
                      <th className="py-3 px-4 text-left">Gross (₦)</th>
                      <th className="py-3 px-4 text-left">Deductions (₦)</th>
                      <th className="py-3 px-4 text-left">Net (₦)</th>
                      <th className="py-3 px-4 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employee.salary?.history?.length > 0 ? (
                      employee.salary.history.map((hist, i) => (
                        <tr key={i} className="border-t border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition">
                          <td className="py-3 px-4">{hist.month} {hist.year}</td>
                          <td className="py-3 px-4">{(hist.grossSalary || 0).toLocaleString()}</td>
                          <td className="py-3 px-4">
                            {Object.values(hist.deductions || {}).reduce((a, b) => a + b, 0).toLocaleString() || 0}
                          </td>
                          <td className="py-3 px-4">{(hist.netSalary || 0).toLocaleString()}</td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => {
                                setSelectedHistory(hist);
                                setIsPayslipModalOpen(true);
                              }}
                              className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition duration-200"
                            >
                              View Payslip
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="py-3 px-4 text-center text-gray-500 dark:text-gray-400">
                          No salary history available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Apply for Leave */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-semibold text-gray-700 dark:text-gray-100 mb-4">Apply for Leave</h3>
              <form onSubmit={handleRequestLeave} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow-inner">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input
                    name="startDate"
                    type="date"
                    required
                    className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-800 dark:text-gray-100"
                  />
                  <input
                    name="endDate"
                    type="date"
                    required
                    className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-800 dark:text-gray-100"
                  />
                  <select
                    name="reason"
                    required
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg col-span-2 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="" disabled>
                      Select Reason
                    </option>
                    <option value="Medicals">Medicals</option>
                    <option value="Burial">Burial</option>
                    <option value="Wedding">Wedding</option>
                    <option value="Studies">Studies</option>
                    <option value="Others">Others</option>
                  </select>
                  {reason === 'Others' && (
                    <input
                      name="others"
                      type="text"
                      placeholder="Please specify"
                      required
                      className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg col-span-2 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-800 dark:text-gray-100"
                    />
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isLeaveSubmitting}
                  className={`w-full flex items-center justify-center px-4 py-2 rounded-lg text-white transition duration-200 ${
                    isLeaveSubmitting ? 'bg-orange-300 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600'
                  }`}
                >
                  {isLeaveSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    'Request Leave'
                  )}
                </button>
              </form>
            </div>

            {/* Leave Requests */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-semibold text-gray-700 dark:text-gray-100 mb-4">Leave Requests</h3>
              <div className="space-y-4">
                {employee.leaveRequests?.length > 0 ? (
                  employee.leaveRequests.map((leave) => (
                    <div
                      key={leave._id}
                      className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow hover:shadow-md transition duration-200"
                    >
                      <p className="text-gray-700 dark:text-gray-100">
                        {leave.reason} ({new Date(leave.startDate).toLocaleDateString()} to{' '}
                        {new Date(leave.endDate).toLocaleDateString()}) - Date Requested:{' '}
                        {new Date(leave.createdAt).toLocaleDateString()} -{' '}
                        <span
                          className={
                            leave.status === 'Approved'
                              ? 'text-green-600 font-medium'
                              : leave.status === 'Rejected'
                              ? 'text-red-600 font-medium'
                              : 'text-yellow-600 font-medium'
                          }
                        >
                          Status: {leave.status}
                        </span>
                      </p>
                      {leave.response && <p className="text-gray-600 dark:text-gray-400 mt-1">Response: {leave.response}</p>}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center">No leave requests available</p>
                )}
              </div>
            </div>

            {/* Attendance */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-semibold text-gray-700 dark:text-gray-100 mb-4">Attendance</h3>
              <EmployeeAttendance employeeId={employee?._id} attendanceRecords={mockAttendanceRecords} />
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <p className="text-gray-500 dark:text-gray-400">No employee data available. Please contact your employer.</p>
          </div>
        )}

        {/* Payslip Modal */}
        {isPayslipModalOpen && payslipData.employee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-100">Payslip</h3>
                <button
                  onClick={() => {
                    setIsPayslipModalOpen(false);
                    setSelectedHistory(null);
                  }}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <BiX size={24} />
                </button>
              </div>
              <div className="space-y-4">
                <p className="text-gray-700 dark:text-gray-100">
                  <strong>Employee:</strong> {payslipData.employee}
                </p>
                <p className="text-gray-700 dark:text-gray-100">
                  <strong>Gross Salary:</strong> ₦{(payslipData.salary.grossSalary || 0).toLocaleString()}
                </p>
                <p className="text-gray-700 dark:text-gray-100">
                  <strong>Deductions:</strong> ₦{
                    Object.values(payslipData.salary.deductions || {})
                      .reduce((a, b) => a + b, 0)
                      .toLocaleString() || 0
                  }
                </p>
                <p className="text-gray-700 dark:text-gray-100">
                  <strong>Benefits Total:</strong> ₦{(payslipData.benefits.total || 0).toLocaleString()}
                </p>
                <p className="text-gray-700 dark:text-gray-100">
                  <strong>Net Salary:</strong> ₦{(payslipData.salary.netSalary || 0).toLocaleString()}
                </p>
                <div>
                  <strong className="text-gray-700 dark:text-gray-100">Breakdown:</strong>
                  <table className="w-full mt-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <thead className="bg-orange-500 text-white">
                      <tr>
                        <th className="py-2 px-3 text-left">Type</th>
                        <th className="py-2 px-3 text-left">Amount (₦)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(calculateBreakdown(payslipData.salary.deductions)).map(([type, amount]) => (
                        <tr key={type} className="border-t border-gray-200 dark:border-gray-600">
                          <td className="py-2 px-3 capitalize text-gray-700 dark:text-gray-100">{type}</td>
                          <td className="py-2 px-3 text-gray-700 dark:text-gray-100">{(amount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-gray-200 dark:border-gray-600">
                        <td className="py-2 px-3 capitalize text-gray-700 dark:text-gray-100">Essential Benefit</td>
                        <td className="py-2 px-3 text-gray-700 dark:text-gray-100">{(payslipData.benefits.essential || 0).toLocaleString()}</td>
                      </tr>
                      <tr className="border-t border-gray-200 dark:border-gray-600">
                        <td className="py-2 px-3 capitalize text-gray-700 dark:text-gray-100">Health Benefit</td>
                        <td className="py-2 px-3 text-gray-700 dark:text-gray-100">{(payslipData.benefits.health || 0).toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={() => {
                    const element = document.createElement('a');
                    const file = new Blob(
                      [
                        `Payslip for ${payslipData.employee}\n` +
                        `Gross: ₦${(payslipData.salary.grossSalary || 0).toLocaleString()}\n` +
                        `Deductions: ₦${Object.values(payslipData.salary.deductions || {})
                          .reduce((a, b) => a + b, 0)
                          .toLocaleString()}\n` +
                        `Benefits Total: ₦${(payslipData.benefits.total || 0).toLocaleString()}\n` +
                        `Net: ₦${(payslipData.salary.netSalary || 0).toLocaleString()}`
                      ],
                      { type: 'text/plain' }
                    );
                    element.href = URL.createObjectURL(file);
                    element.download = `payslip_${payslipData.employee.replace(/ /g, '_')}_${selectedHistory?.month || 'latest'}_${selectedHistory?.year || new Date().getFullYear()}.txt`;
                    document.body.appendChild(element);
                    element.click();
                    document.body.removeChild(element);
                  }}
                  className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 flex items-center gap-2 w-full transition duration-200"
                >
                  <BiDownload /> Download Payslip
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployerEmployeeJobSeeker;